import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as MessageIcon } from "../../../../../../assets/Profile/specialist/message.svg";
import CircleAvatar from "../../../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../../../components/Input/Input";
import MultipleSelect from "../../../../../../components/MultipleSelect/MultipleSelect";
import { ContextProvider } from "../../../../../../contextProvider";
import { ProjectRoles } from "../../../../../../types/Company";
import { MainSpecializations } from "../../../../../../types/TypeSpecialist";
import { GeneralMultiLangText } from "../../../../../../types/TypeUsers";
import { formatDateAndTime } from "../../../../../../utils/dateActions";
import { getTranslation } from "../../../../../../utils/getTranslation";
import s from "./InviteStatistic.module.css";

type InviteStatisticProps = {
  entryCodes: {
    code: string;
    roles: ProjectRoles[];
    forSpecialist?: string;
    forEmail?: string;
    forSpecializations?: MainSpecializations[];
    isAccepted: boolean;
    isRejected: boolean;
    sentAt: string;
    answeredAt: string;
    user: {
      _id: string;
      name: GeneralMultiLangText[];
      surname: GeneralMultiLangText[];
    };
  }[];

  setModalIsOpen: Dispatch<SetStateAction<boolean>>;
};

const getStatus = (isAccepted: boolean, isRejected: boolean) => {
  if (isAccepted) {
    return {
      className: s.acceptStatus,
      title: "Accepted",
    };
  }
  if (isRejected) {
    return {
      className: s.rejectStatus,
      title: "Rejected",
    };
  }
  if (!isAccepted && !isRejected) {
    return {
      className: s.awaitStatus,
      title: "Not answered",
    };
  }
};

const InviteStatistic = ({
  entryCodes,
  setModalIsOpen,
}: InviteStatisticProps) => {
  const { setUserIdForCreate } = useContext(ContextProvider);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredData = useMemo(() => {
    return entryCodes
      .filter((el) => el.user)
      .filter((row) => {
        const fullName =
          getTranslation(row.user.name, "en") +
          " " +
          getTranslation(row.user.surname, "en");
        const email = row.forEmail ? row.forEmail?.toLowerCase() : "";
        const matchesSearch =
          fullName.toLowerCase().includes(search.toLowerCase()) ||
          email.includes(search.toLowerCase());

        const status = getStatus(row.isAccepted, row.isRejected)?.title;
        const matchesStatus = statusFilter === "All" || status === statusFilter;

        return matchesSearch && matchesStatus;
      })
      .sort(
        (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
      );
  }, [entryCodes, search, statusFilter]);

  const filterStatusData = [
    {
      lable: "All",
      value: "All",
    },
    {
      lable: "Accepted",
      value: "Accepted",
    },
    {
      lable: "Rejected",
      value: "Rejected",
    },
    {
      lable: "Not answered",
      value: "Not answered",
    },
  ];

  return (
    <div className={s.container}>
      <span className={s.filtersTitle}>Filters</span>
      <div className={s.filters}>
        <Input
          inputValue={search}
          onChangeInput={(value) => setSearch(value)}
          isVisible
          required
          placeholder="Search by name or email"
          width="25%"
        />
        <MultipleSelect
          data={filterStatusData}
          multiplie={false}
          setValue={(value) => setStatusFilter(value as string)}
          value={statusFilter}
          disableSortData
          width="25%"
        />
      </div>
      <div className={s.tableBlock}>
        <table className={s.table}>
          <thead>
            <tr className={s.headerRow}>
              <th>Name</th>
              <th>Specialization</th>
              <th>Email</th>
              <th>Sent Date</th>
              <th>Answer Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredData && filteredData.length > 0 ? (
              filteredData.map((row, i) => (
                <tr key={i} className={s.row}>
                  <td>
                    <div className={s.nameAvatarBlock}>
                      <CircleAvatar userId={row.user._id} />
                      <span>
                        {getTranslation(row.user.name, "en") +
                          " " +
                          getTranslation(row.user.surname, "en")}
                      </span>
                    </div>
                  </td>
                  <td>
                    {row.forSpecializations
                      ? row.forSpecializations
                          .map((item) => item.specialization)
                          .join(", ")
                      : " - "}
                  </td>
                  <td>{row.forEmail}</td>
                  <td>
                    {
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedDate
                    }
                    ,{" "}
                    {
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedTime
                    }
                  </td>
                  <td>
                    {row.answeredAt &&
                      formatDateAndTime(new Date(row.sentAt), "en")
                        .formattedDate +
                        ", " +
                        formatDateAndTime(new Date(row.sentAt), "en")
                          .formattedTime}
                  </td>
                  <td>
                    <div className={s.statusBlock}>
                      <div
                        className={`${s.status} ${
                          getStatus(row.isAccepted, row.isRejected)?.className
                        }`}
                      >
                        {getStatus(row.isAccepted, row.isRejected)?.title}
                      </div>
                      <Link
                        to="/chat"
                        onClick={() => {
                          setUserIdForCreate(row.user._id);
                          setModalIsOpen(false);
                        }}
                        className={s.sendMessageBtn}
                      >
                        {" "}
                        <MessageIcon />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className={s.emptyState}>
                  Invitations not found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InviteStatistic;
